import { FailureNotice } from '@/components/failure-notice';
import { campaignPublishingFailure, providerLabel } from '@/lib/campaign-publish-failure';
import { OverlayLeaveButton, ResponsiveOverlay } from '@/components/responsive-overlay';
import { useEffect, useState } from 'react';
import { Icons } from '@/components/primitives';
import { campaignPublishOutcome } from '@/api/manage-hooks';
import type {
  CampaignProviderState,
  CampaignSyncBlock,
  CampaignSyncStatus,
  CampaignSyncTarget,
} from '@/api/manage-types';
import { PublishSequence } from '@/components/publish-sequence';
import type { PublishStep } from '@/lib/campaign-publish-sequence';
import type { AudiencePreviewState } from './audience';

// One definition, in the lib module the publish failure copy already reads.
export { providerLabel };

/**
 * What an audience size is read from: a slot's own preview, or the parts of one
 * the campaign-level estimate supplies (count, reach, pending, error).
 */
export type AudienceSizeSnapshot = Partial<Pick<
  AudiencePreviewState,
  'count' | 'totalReach' | 'isProspecting' | 'modelStatus' | 'isPending' | 'error'
>>;

export function settledAudienceSize(snapshot: AudienceSizeSnapshot | null | undefined): number | null {
  if (
    snapshot?.isProspecting
    && (snapshot?.modelStatus && snapshot.modelStatus !== 'ready'
      || snapshot?.count === 0 && snapshot?.totalReach === 0)
  ) return null;
  return !snapshot?.isPending
    && !snapshot?.error
    && typeof snapshot?.count === 'number'
    ? snapshot.count
    : null;
}

/**
 * A completed publish does not mean the provider is delivering: the hierarchy is
 * created PAUSED and only activated when a flight covers today, so `synced` plus
 * a non-active `provider_state` is a campaign that will not spend yet.
 */
export interface CampaignDeliveryStateCopy {
  label: string;
  /** Why it is not delivering, as a clause; null for the one state that is. */
  detail: string | null;
}

export const CAMPAIGN_DELIVERY_STATES: Record<CampaignProviderState, CampaignDeliveryStateCopy> = {
  active: { label: 'Live', detail: null },
  active_pending_delivery: {
    label: 'Activated, not delivering yet',
    detail: 'the hierarchy is active but the provider is still reviewing or billing-gating it',
  },
  scheduled_paused: {
    label: 'Scheduled (paused until flight starts)',
    detail: 'the whole hierarchy stays paused there until a flight covers today',
  },
  paused_superseded: {
    label: 'Paused',
    detail: 'a newer campaign revision superseded this publish and the hierarchy was paused',
  },
  disabled: {
    label: 'Paused',
    detail: 'delivery is turned off for this campaign',
  },
  disabled_superseded: {
    label: 'Paused',
    detail: 'a newer campaign revision superseded this publish and the ad groups were left disabled',
  },
};

function isCampaignProviderState(value: string): value is CampaignProviderState {
  // Own keys only: a state spelled like an Object.prototype member
  // (`constructor`, `toString`) is not one this build recognizes.
  return Object.hasOwn(CAMPAIGN_DELIVERY_STATES, value);
}

/** What the modal says about delivery for one destination. */
export interface CampaignDeliveryReading {
  /** The provider's state as reported, or null when none was. */
  state: string | null;
  label: string;
  detail: string | null;
  delivering: boolean;
  /** Whether the response made a delivery claim at all. */
  reported: boolean;
}

/**
 * The part of a sync answer the delivery reading looks at. `provider_state` is
 * read as `unknown` on purpose: this is the guard against an older or malformed
 * body, so it must not trust the declared type it is guarding.
 */
export type CampaignDeliveryInput = Partial<Record<
  CampaignSyncTarget,
  (Omit<CampaignSyncBlock, 'provider_state'> & { provider_state?: unknown }) | null
>>;

export function campaignDeliveryState(
  status: CampaignDeliveryInput | null | undefined,
  target: CampaignSyncTarget,
): CampaignDeliveryReading {
  const providerState = status?.[target]?.provider_state;
  // A response with no `provider_state` — an older server, or a partial or
  // malformed body — carries no delivery claim at all. That is not evidence of
  // delivery, so report it as indeterminate rather than borrowing the old
  // "synced means live" reading. `reported` tells callers whether there is a
  // delivery claim to render at all, so the indeterminate case can be styled as
  // neither live nor a confirmed pause.
  if (typeof providerState !== 'string') {
    return {
      state: null,
      label: 'Delivery state unknown',
      detail: `${providerLabel(target)} did not report a delivery state for this publish`,
      delivering: false,
      reported: false,
    };
  }
  if (!isCampaignProviderState(providerState)) {
    return {
      state: providerState,
      label: 'Not delivering',
      detail: `${providerLabel(target)} reported a delivery state this build does not recognize`,
      delivering: false,
      reported: true,
    };
  }
  const known = CAMPAIGN_DELIVERY_STATES[providerState];
  return {
    state: providerState,
    label: known.label,
    detail: known.detail,
    delivering: providerState === 'active',
    reported: true,
  };
}

/**
 * The sync answer a launch may speak for, or `null` while none has landed.
 *
 * The question is "was this response REQUESTED after the launch", and
 * `dataUpdatedAt` cannot answer it: react-query stamps it when a response
 * LANDS. `campaignSyncStatus` polls at CADENCE.fast for every saved campaign,
 * so a request issued a moment before the launch lands a few hundred ms after
 * `startedAt` — and a bare `dataUpdatedAt >= startedAt` accepted it as the
 * first post-launch answer. It describes the campaign BEFORE the publish, both
 * provider legs still null, which `campaignPublishOutcome` reads as the
 * intentional local-ready mode: the modal offered "Done · go to campaigns" for
 * a publish nothing had confirmed, on every publish.
 *
 * So the launch records what the poll already knew (`baselineUpdatedAt`) and
 * whether it had a request out (`pollInFlightAtLaunch`), and this discounts the
 * answer that request still owes. react-query runs at most one fetch per key,
 * so exactly one answer can be owed; every landing after it belongs to a
 * request issued later, hence after the launch.
 *
 * If that in-flight request fails instead of landing, the discount falls on the
 * next poll instead and the modal shows progress for one extra interval. That
 * is the safe direction: late progress, never an unearned verdict.
 */
export interface PublishProgress {
  campaignId: string;
  target: CampaignSyncTarget;
  /** `Date.now()` when the launch began. */
  startedAt: number;
  /** The sync poll's `dataUpdatedAt` at that instant, 0 when it had none. */
  baselineUpdatedAt: number;
  /** Whether the poll had a request out at that instant. */
  pollInFlightAtLaunch: boolean;
}

/** The parts of the sync-status query the guard reads. */
export interface SyncPollSnapshot {
  data: CampaignSyncStatus | undefined;
  dataUpdatedAt: number;
}

/** The landing a launch discounted, and which launch it was owed to. */
interface OwedLanding {
  token: number;
  landedAt: number;
}

export function usePostLaunchSyncSnapshot(
  progress: PublishProgress | null | undefined,
  query: SyncPollSnapshot | null | undefined,
): CampaignSyncStatus | null {
  const token = progress?.startedAt ?? null;
  const baselineUpdatedAt = progress?.baselineUpdatedAt ?? 0;
  const owesStaleLanding = progress?.pollInFlightAtLaunch ?? false;
  const landedAt = query?.dataUpdatedAt ?? 0;
  // Which landing was the owed one, scoped to the launch that owed it.
  const [discounted, setDiscounted] = useState<OwedLanding | null>(null);
  const discountedAt = discounted?.token === token ? discounted.landedAt : 0;
  useEffect(() => {
    if (token === null || !owesStaleLanding) return;
    if (landedAt <= baselineUpdatedAt) return;
    // Idempotent: the first landing past the baseline is the owed one, and
    // re-running this effect — StrictMode's replay included — names the same
    // one rather than consuming another.
    setDiscounted((current) =>
      current?.token === token ? current : { token, landedAt },
    );
  }, [token, owesStaleLanding, landedAt, baselineUpdatedAt]);

  if (!progress || !query?.data) return null;
  // Landed before the launch began, so it cannot describe it.
  if (landedAt < progress.startedAt) return null;
  // The answer the launch started from. Kept alongside the clock test above:
  // the two fail on different sources of staleness.
  if (landedAt <= baselineUpdatedAt) return null;
  if (owesStaleLanding && (discountedAt === 0 || landedAt <= discountedAt)) return null;
  return query.data;
}

export interface CampaignPublishingModalProps {
  publishSteps?: PublishStep[];
  campaignName: string;
  target: CampaignSyncTarget;
  /** The first sync answer that describes this launch; null until one lands. */
  snapshot: CampaignSyncStatus | null;
  statusCheckFailed: boolean;
  refreshing: boolean;
  onDone: () => void;
  onRetry: () => void;
  onDismissFailure: () => void;
}

export function CampaignPublishingModal({ publishSteps = [],
  campaignName,
  target,
  snapshot,
  statusCheckFailed,
  refreshing,
  onDone,
  onRetry,
  onDismissFailure,
}: CampaignPublishingModalProps) {
  const outcome = snapshot
    ? campaignPublishOutcome(snapshot, target)
    : 'pending';
  const block = snapshot?.[target] ?? null;
  const label = providerLabel(target);
  const checking = snapshot === null;
  const failed = !checking && outcome === 'failed';
  const synced = !checking && outcome === 'synced';
  const localReady = !checking && outcome === 'local_ready';
  const delivery = campaignDeliveryState(snapshot, target);
  // Three outcomes, not two: delivering, held paused, or no delivery claim at
  // all. The last one must not borrow either of the other two readings.
  const deliveryUnknown = synced && !delivery.reported;
  const heldPaused = synced && !delivery.delivering && delivery.reported;
  const statusLabel = checking
    ? 'Checking queue status'
    : localReady
      ? 'No provider destination queued'
      : block?.status === 'pending'
        ? 'Queued'
        : block?.status === 'syncing'
          ? 'Publishing'
          : block?.status === 'synced'
            ? delivery.label
            : 'Failed';
  const title = failed
    ? `${label} publishing needs attention`
    : deliveryUnknown
      ? `${label} accepted the campaign, but its delivery state is unknown`
      : heldPaused
        ? `${label} accepted the campaign, but it is not delivering`
        : synced
          ? `${label} campaign published`
          : localReady
            ? 'Campaign ready to publish'
            : `Publishing campaign to ${label}`;

  return (
    <ResponsiveOverlay label="Publishing progress" kind="progress" className="progress-host" dismissible={failed || synced || localReady} onClose={() => { if (failed) onDismissFailure(); else if (synced || localReady) onDone(); }}>
      <div
        className="launch-dialog"
        aria-labelledby="campaign-publish-title"
        aria-describedby="campaign-publish-description"
      >
        <div className="launch-dialog-hd">
          <h3 id="campaign-publish-title">{title}</h3>
          <div className="sub" id="campaign-publish-description">
            {failed
              /* A failure here can be a read-back that could not confirm an activation the
                 provider accepted, so it says nothing about delivery either way (Astra F5 A1). */
              ? `Publishing ${campaignName || 'this campaign'} could not be confirmed, so no provider success has been assumed. ${label} may already be delivering it — check the campaign there before assuming either way.`
              : deliveryUnknown
                ? `${label} confirmed the campaign hierarchy, but ${delivery.detail}. Whether it is delivering is unknown — check the campaign in ${label} before assuming it is spending.`
                : heldPaused
                  ? `${label} confirmed the campaign hierarchy, but ${delivery.detail}. Nothing is spending yet.`
                  : synced
                    ? `${label} confirmed the campaign hierarchy and its external identifiers.`
                    : localReady
                      ? 'Provider publishing is disabled for this deployment, so the validated plan remains in the local ready_to_publish state.'
                      : `Keep this window open while ${label} creates and verifies the campaign hierarchy.`}
          </div>
        </div>
        <div className="launch-dialog-body" aria-live="polite">
          <div className="lbl">Live publishing status</div>
          {/* The same sequence the right column shows, at full density. It used
              to be one line reading e.g. "Meta destination · Publishing", which
              could not say which of the eight steps was running, what had
              already succeeded, or why a finished publish was not serving. */}
          {publishSteps && publishSteps.length > 0 ? (
            <PublishSequence steps={publishSteps}/>
          ) : (
            <div className="muted" style={{fontSize:11.5,lineHeight:1.5,padding:'8px 0'}}>
              {checking
                ? 'Checking queue status…'
                : localReady
                  ? 'Provider publishing is disabled for this deployment, so the validated plan remains in the local ready_to_publish state.'
                  : `Waiting for ${label} to report the first stage.`}
            </div>
          )}
          {/* The campaign-level delivery claim, kept beside the sequence rather
              than folded into it. `synced` only says the publish converged, so
              this stays a THREE-way distinction — delivering, held paused, or
              no delivery claim at all — and the third must never borrow the
              reading of either other. */}
          {!checking && (
            <div className="muted" style={{fontSize:11.5,lineHeight:1.45,marginTop:8}}>
              <b>{localReady ? 'Publishing handoff' : `${label} destination`}</b>
              {' · '}{statusLabel}
              {block && block.attempts > 0 && (
                <> · {block.attempts} failed {block.attempts === 1 ? 'attempt' : 'attempts'}</>
              )}
            </div>
          )}
          {failed && (
            <div style={{marginTop:8}}>
              <FailureNotice
                dense
                announce={false}
                failure={campaignPublishingFailure(snapshot, target).failure}
                fallback={campaignPublishingFailure(snapshot, target).text}
              />
            </div>
          )}
          {checking && statusCheckFailed && (
            <div style={{color:'var(--bad)',fontSize:11.5,lineHeight:1.45,marginTop:10}}>
              Publishing started, but the latest status check could not be
              completed. No success has been assumed.
            </div>
          )}
        </div>
        <div className="launch-dialog-foot">
          {failed ? (
            <>
              <button className="btn" type="button" onClick={onDismissFailure}>
                Stay on campaign
              </button>
              <button
                className="btn btn-primary"
                type="button"
                disabled={refreshing}
                onClick={onRetry}
              >
                {Icons.refresh} {refreshing ? 'Checking…' : 'Check status again'}
              </button>
            </>
          ) : synced || localReady ? (
            <OverlayLeaveButton className="btn btn-primary" onLeave={onDone}>
              {Icons.check} Done · go to campaigns
            </OverlayLeaveButton>
          ) : (
            <>
              <span className="muted" style={{fontSize:11.5}}>
                {statusCheckFailed
                  ? 'Retrying the status check automatically…'
                  : 'Checking again automatically…'}
              </span>
              {statusCheckFailed && (
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={refreshing}
                  onClick={onRetry}
                >
                  {Icons.refresh} {refreshing ? 'Checking…' : 'Check now'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </ResponsiveOverlay>
  );
}
